import { html, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { GzClearableInputWebComponent } from './GzClearableInputWebComponent';
import { InputEvents } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { Keys } from '../../../types/common/types';
import { styles as searchInputStyles } from '../view/input-search.scss';
import { GzInputWebComponent } from './GzInputWebComponent';
import IconSet from '../../gzIcon/GzIconSet';

@customElement('gz-input-search')
export class GzInputSearchWebComponent extends GzClearableInputWebComponent {
  /**
   * Set this property to TRUE for triggering the SEARCH event when the magnifier icon button is clicked or the user
   * presses ENTER
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  searchOnAction = false;

  @property({ type: String, attribute: true, reflect: true })
  prefixIcon: string;

  static get styles() {
    return [...GzInputWebComponent.styles, unsafeCSS(`${searchInputStyles}`)];
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('value')) {
      this.setCanDelete(this.value.length > 0);
    }
  }

  protected renderSearchButton() {
    return html`
      <gz-icon-button
        ?disabled="${!this.searchOnAction}"
        id="searchIcon"
        @click="${this.onInputSearch}"
        class="input--button input--search"
        icon="ds-icon-search"
        size="24"
        fontsize="20"
        buttontype="square"
        buttonfill="quiet"
        buttoncolor="secondary"
      >
      </gz-icon-button>
    `;
  }

  protected handleInputEvent(event: Event) {
    event.stopImmediatePropagation();
  }

  private renderPrefixIcon(): TemplateResult | undefined {
    if (isEmpty(this.prefixIcon)) {
      return;
    }

    const iconSetInstance = IconSet.getInstance();
    if (iconSetInstance.isSVGIconAvailable(this.prefixIcon)) {
      return html`<gz-icon .name="${this.prefixIcon}" size="16" color="secondary" class="input--prefixIcon"></gz-icon>`;
    } else {
      return html`<span id="prefixIcon" part="icon" class="${this.prefixIcon} input--prefixIcon"></span> `;
    }
  }

  protected renderInputElements() {
    return html`${this.renderPrefixIcon()} ${this.canDelete ? this.renderClearButton() : this.renderSearchButton()}`;
  }

  protected onUserInput() {
    if (this.searchOnAction) {
      return;
    }
    super.onUserInput();
  }

  protected onKeyUp(event: KeyboardEvent) {
    if (this.searchOnAction) {
      this.setCanDelete(false);

      if (event.key === Keys.Enter) {
        this.onInputSearch();
      }
    } else {
      super.onKeyUp(event);
      this.emitInputEvent(InputEvents.CHANGE);
    }
  }

  protected onInputSearch() {
    this.inputElement.blur();
    this.emitInputEvent(InputEvents.SEARCH);
    this.value = this.inputElement.value;
    this.setCanDelete(true);
  }

  public clearInputValue(e: Event) {
    super.clearInputValue(e);
    if (this.searchOnAction) {
      this.emitInputEvent(InputEvents.SEARCH);
    }
  }
}
